import { cn } from "@/lib/utils";
import { Logo } from "components/core/Logo";
import Image from "next/image";
import { ComponentProps, useEffect, useState } from "react";

export const ContentThumbnailImg = ({
    src,
    onFallbackChange,
    className,
    priority = false,
    ...props
}: {
    className?: string;
    onFallbackChange?: (showFallback: boolean) => void;
    priority?: boolean;
} & Omit<ComponentProps<typeof Image>, "alt">) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    // Show fallback if no src or image failed to load
    const showFallback = !src || imageError;

    // Notify parent component when fallback state changes
    useEffect(() => {
        onFallbackChange?.(showFallback);
    }, [showFallback, onFallbackChange]);

    if (showFallback) {
        return (
            <div
                className={cn(
                    `flex h-[70px] w-[70px] items-center justify-center
                    rounded-md bg-grey-100 dark:bg-grey-50 md:h-full md:w-full`,
                    className
                )}
            >
                <Logo
                    className="h-10 w-10 text-grey-800 opacity-40
                        dark:text-grey-200 md:w-20"
                />
            </div>
        );
    }

    return (
        <Image
            className={cn(
                "rounded-xl object-cover object-center",
                // Placeholder while the image downloads, so the card is not an empty hole
                !imageLoaded && "bg-grey-200 dark:bg-grey-50",
                className
            )}
            alt="thumbnail"
            src={src}
            preload={priority}
            fetchPriority={priority ? "high" : undefined}
            sizes="(max-width: 768px) 70px, (max-width: 1024px) 150px, 200px"
            onError={() => setImageError(true)}
            onLoad={() => setImageLoaded(true)}
            {...props}
        />
    );
};
